import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Ship, TrendingUp } from "lucide-react";
import { api } from "../../lib/api";
import { Modal, IconBtn, AddBtn, inputCls } from "./ui";

const gh = (n) => `GH₵ ${Math.round(Number(n) || 0).toLocaleString()}`;

export default function Shipments() {
  const [rows, setRows] = useState([]);
  const [margin, setMargin] = useState(null);
  const [form, setForm] = useState(null);
  const load = () => {
    api.get("/admin/shipments").then((r) => setRows(r.data)).catch(() => {});
    api.get("/admin/shipments/margin").then((r) => setMargin(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const del = async (id) => {
    if (!window.confirm("Delete this shipment?")) return;
    await api.delete(`/admin/shipments/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div data-testid="admin-shipments">
      <div className="flex justify-between items-center gap-2 mb-5">
        <div>
          <h2 className="font-display text-2xl text-[#0D530E]">Shipments</h2>
          <p className="text-sm text-[#5C5C5C]">Track each real consolidation (Jobyco/Samamiah) and its margin vs shipping collected.</p>
        </div>
        <AddBtn label="New shipment" onClick={() => setForm({})} testid="add-shipment-btn" icon={Plus} />
      </div>

      {margin && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="shipping-margin">
          <div className="rounded-2xl bg-white border border-black/5 p-4">
            <p className="text-xs uppercase tracking-wider text-[#5C5C5C] font-semibold">Shipping collected (all paid orders)</p>
            <p className="font-display text-2xl text-[#0D530E] mt-1">{gh(margin.collected_ghs)}</p>
          </div>
          <div className="rounded-2xl bg-white border border-black/5 p-4">
            <p className="text-xs uppercase tracking-wider text-[#5C5C5C] font-semibold">Provider cost (all shipments)</p>
            <p className="font-display text-2xl text-[#D9933B] mt-1">{gh(margin.provider_cost_ghs)}</p>
          </div>
          <div className="rounded-2xl bg-white border border-black/5 p-4">
            <p className="text-xs uppercase tracking-wider text-[#5C5C5C] font-semibold flex items-center gap-1"><TrendingUp size={13} /> Shipping margin</p>
            <p className={`font-display text-2xl mt-1 ${margin.margin_ghs >= 0 ? "text-[#306D29]" : "text-[#B23B3B]"}`}>{gh(margin.margin_ghs)} · {margin.margin_pct}%</p>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {rows.length === 0 && <p className="text-sm text-[#5C5C5C]">No shipments yet.</p>}
        {rows.map((s) => (
          <div key={s.id} className="bg-white border border-black/5 rounded-2xl p-4 flex items-center gap-4" data-testid={`shipment-${s.id}`}>
            <span className="w-11 h-11 rounded-xl bg-[#306D29]/10 text-[#306D29] flex items-center justify-center shrink-0"><Ship size={18} /></span>
            <div className="flex-1 min-w-0">
              <p className="font-display text-lg text-[#0D530E] truncate">{s.reference || "Shipment"} · {s.provider || "—"}</p>
              <p className="text-xs text-[#5C5C5C]">{s.method?.toUpperCase()} · {s.destination || "—"} · {s.container || "—"} · {(s.order_numbers || []).length} orders · <span className="capitalize">{s.status?.replace("_", " ")}</span></p>
            </div>
            <div className="text-right shrink-0 hidden sm:block">
              <p className="text-xs text-[#5C5C5C]">collected {gh(s.collected_ghs)} · cost {gh(s.provider_cost_ghs)}</p>
              <p className={`text-sm font-semibold ${s.margin_ghs >= 0 ? "text-[#306D29]" : "text-[#B23B3B]"}`}>margin {gh(s.margin_ghs)}</p>
            </div>
            <div className="flex gap-0.5 shrink-0">
              <IconBtn title="Edit" testid={`edit-shipment-${s.id}`} onClick={() => setForm(s)}><Pencil size={15} /></IconBtn>
              <IconBtn title="Delete" testid={`del-shipment-${s.id}`} onClick={() => del(s.id)}><Trash2 size={15} className="text-[#B23B3B]" /></IconBtn>
            </div>
          </div>
        ))}
      </div>

      {form && <ShipmentForm shipment={form} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
    </div>
  );
}

function ShipmentForm({ shipment, onClose, onSaved }) {
  const editing = !!shipment.id;
  const [f, setF] = useState({
    reference: shipment.reference || "", provider: shipment.provider || "", method: shipment.method || "sea",
    destination: shipment.destination || "", container: shipment.container || "",
    provider_cost_gbp: shipment.provider_cost_gbp || "", status: shipment.status || "in_transit",
    order_numbers: (shipment.order_numbers || []).join(", "), notes: shipment.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    const body = {
      reference: f.reference, provider: f.provider, method: f.method, destination: f.destination,
      container: f.container, provider_cost_gbp: Number(f.provider_cost_gbp) || 0, status: f.status,
      order_numbers: f.order_numbers.split(",").map((x) => x.trim()).filter(Boolean), notes: f.notes,
    };
    try {
      if (editing) await api.put(`/admin/shipments/${shipment.id}`, body); else await api.post("/admin/shipments", body);
      toast.success(editing ? "Shipment saved" : "Shipment added"); onSaved();
    } catch { toast.error("Failed to save"); setSaving(false); }
  };
  return (
    <Modal title={editing ? "Edit shipment" : "New shipment"} onClose={onClose} testid="shipment-form">
      <form onSubmit={submit} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <input required className={inputCls} placeholder="Reference (e.g. AF1024)" value={f.reference} onChange={set("reference")} data-testid="sf-reference" />
          <input className={inputCls} placeholder="Provider (Jobyco / Samamiah)" value={f.provider} onChange={set("provider")} data-testid="sf-provider" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <select className={inputCls} value={f.method} onChange={set("method")} data-testid="sf-method">
            <option value="sea">Sea</option><option value="air">Air</option>
          </select>
          <input className={inputCls} placeholder="Destination" value={f.destination} onChange={set("destination")} data-testid="sf-destination" />
          <input className={inputCls} placeholder="Container (e.g. XXL)" value={f.container} onChange={set("container")} data-testid="sf-container" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-semibold text-[#5C5C5C]">Provider cost (£)</label>
            <input type="number" step="0.01" className={inputCls} placeholder="110" value={f.provider_cost_gbp} onChange={set("provider_cost_gbp")} data-testid="sf-cost" />
          </div>
          <div>
            <label className="text-xs font-semibold text-[#5C5C5C]">Status</label>
            <select className={inputCls} value={f.status} onChange={set("status")} data-testid="sf-status">
              <option value="preparing">Preparing</option>
              <option value="in_transit">In transit</option>
              <option value="arrived">Arrived</option>
              <option value="delivered">Delivered</option>
            </select>
          </div>
        </div>
        <div>
          <label className="text-xs font-semibold text-[#5C5C5C]">Order numbers in this shipment (comma separated — shipping collected is summed from these paid orders)</label>
          <textarea className={inputCls} rows={2} placeholder="1024, 1025, 1026" value={f.order_numbers} onChange={set("order_numbers")} data-testid="sf-orders" />
        </div>
        <textarea className={inputCls} rows={2} placeholder="Notes" value={f.notes} onChange={set("notes")} data-testid="sf-notes" />
        <button type="submit" disabled={saving} data-testid="sf-submit" className="w-full bg-[#306D29] hover:bg-[#0D530E] text-white font-semibold py-3 rounded-full disabled:opacity-60" style={{ transition: "background-color 0.2s ease" }}>{saving ? "Saving…" : editing ? "Save shipment" : "Add shipment"}</button>
      </form>
    </Modal>
  );
}
